import Add from "@mui/icons-material/Add";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { WasteStreamRegistrationDisplayStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { MouseEvent } from "react";

function missingCodesLabel(missingFederalWasteCodes: string[]): string {
  return `Missing from the registration: ${missingFederalWasteCodes.join(", ")}`;
}

/**
 * The open registration submission's link, a pill matching the "Update
 * needed" chip it replaces once the submission exists. An update submission
 * also carries the missing-codes tooltip and accessible name that the
 * (now-suppressed) "Update needed" chip would show; a new-registration
 * submission only differs by label.
 */
function OpenSubmissionAffordance({
  submissionId,
  isUpdate,
  onOpenSubmission,
  missingFederalWasteCodes,
}: {
  submissionId: string;
  isUpdate: boolean;
  onOpenSubmission: (submissionId: string) => void;
  missingFederalWasteCodes?: string[];
}) {
  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onOpenSubmission(submissionId);
  };

  const label =
    isUpdate && missingFederalWasteCodes?.length
      ? missingCodesLabel(missingFederalWasteCodes)
      : undefined;
  const chip = (
    <StatusChip
      color="info"
      variant="outlined"
      label={isUpdate ? "Update in progress" : "Submission"}
      icon={<OpenInNew fontSize="small" />}
      onClick={handleOpen}
      sx={{ flexShrink: 0 }}
    />
  );

  // describeChild, so the codes become the chip's accessible description rather
  // than replacing "Update in progress" as its name.
  return label ? (
    <Tooltip title={label} describeChild>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
}

export interface WasteStreamRegistrationCellRegistration {
  id: string;
  stateRegisteredId?: string | null;
}

export interface WasteStreamRegistrationCellProps {
  /** Null/absent (rows predating the FK cutover) renders like NOT_APPLICABLE. */
  displayStatus: WasteStreamRegistrationDisplayStatus | null | undefined;
  /** The linked registration. Null unless displayStatus is IN_PROGRESS or REGISTERED. */
  registration: WasteStreamRegistrationCellRegistration | null | undefined;
  /**
   * The stream's open (in-progress) registration submission, if any. When
   * present and staff, a "Submission" link opens it via `onOpenSubmission`.
   */
  openSubmissionId?: string | null;
  /** Staff-only: opens the registration detail drawer for the linked registration. */
  onOpenRegistration: (registrationId: string) => void;
  /** Staff-only: opens the "+ State Registration" matching dialog for this stream. */
  onOpenDialog: () => void;
  /** Staff-only: opens the in-progress registration submission. Absent -> no link. */
  onOpenSubmission?: (submissionId: string) => void;
  /**
   * Additive-code comparison against the linked registration. Null/absent
   * renders no chip.
   */
  flags?: { outdated: boolean; missingFederalWasteCodes: string[] } | null;
  /** The open submission asks the state to add codes rather than register anew. */
  openSubmissionIsUpdate?: boolean;
  /** Staff-only: opens the "Create update submission" dialog for this stream. */
  onCreateUpdateSubmission?: () => void;
}

/**
 * FK-backed "Registration" cell, driven by `registrationDisplayStatus`
 * rather than the legacy scalar triple. The status chip renders for everyone
 * the WasteStateRegistration flag is on for; the interactive open
 * affordances — the status chip's link to the detail drawer (In Progress and
 * Registered alike), the "Submission" link to an in-progress submission, and
 * the "+ State Registration" button — are staff-only, since the
 * queries/mutations they trigger are staff-only and a tenant user must never
 * be able to invoke them. Kept presentational: the grid supplies the
 * callbacks and already has the row in scope to build them. The
 * "Update needed" chip is likewise staff-only, appearing only when the
 * registration is missing federal waste codes the stream now carries.
 */
export function WasteStreamRegistrationCell({
  displayStatus,
  registration,
  openSubmissionId,
  onOpenRegistration,
  onOpenDialog,
  onOpenSubmission,
  flags,
  openSubmissionIsUpdate,
  onCreateUpdateSubmission,
}: WasteStreamRegistrationCellProps) {
  const { isStaff } = useCurrentUser();

  if (!displayStatus || displayStatus === WasteStreamRegistrationDisplayStatus.NotApplicable) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  if (displayStatus === WasteStreamRegistrationDisplayStatus.NeedsRegistration) {
    if (!isStaff) {
      return <StatusChip color="default" variant="outlined" label="Needs Registration" />;
    }
    return (
      <Button
        size="small"
        variant="text"
        startIcon={<Add fontSize="small" />}
        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        onClick={(e) => {
          e.stopPropagation();
          onOpenDialog();
        }}
      >
        State Registration
      </Button>
    );
  }

  // IN_PROGRESS or REGISTERED — a status chip. The state-assigned id lives in
  // its own "State ID" column now (WasteStreamStateIdCell). Staff can click the
  // chip to open the registration detail drawer.
  const isRegistered = displayStatus === WasteStreamRegistrationDisplayStatus.Registered;

  const statusChip = (
    <StatusChip
      color={isRegistered ? "success" : "info"}
      label={isRegistered ? "Registered" : "In Progress"}
      sx={{ flexShrink: 0 }}
    />
  );

  // An open update submission already tracks the missing codes; offering
  // "Update needed" alongside it invites a duplicate submission.
  const hasOpenUpdateSubmission =
    isStaff && !!openSubmissionId && !!onOpenSubmission && !!openSubmissionIsUpdate;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      flexWrap="nowrap"
      sx={{ minWidth: 0, overflow: "hidden" }}
    >
      {isStaff && registration ? (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onOpenRegistration(registration.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              onOpenRegistration(registration.id);
            }
          }}
          sx={{ cursor: "pointer", minWidth: 0 }}
        >
          {statusChip}
        </Stack>
      ) : (
        statusChip
      )}
      {isStaff && openSubmissionId && onOpenSubmission && (
        <OpenSubmissionAffordance
          submissionId={openSubmissionId}
          isUpdate={!!openSubmissionIsUpdate}
          onOpenSubmission={onOpenSubmission}
          missingFederalWasteCodes={flags?.missingFederalWasteCodes}
        />
      )}
      {isStaff && flags?.outdated && onCreateUpdateSubmission && !hasOpenUpdateSubmission && (
        <Tooltip title={missingCodesLabel(flags.missingFederalWasteCodes)} describeChild>
          <StatusChip
            color="warning"
            variant="outlined"
            label="Update needed"
            onClick={(e) => {
              e.stopPropagation();
              onCreateUpdateSubmission();
            }}
            sx={{ flexShrink: 0 }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

export interface WasteStreamStateIdCellProps {
  displayStatus: WasteStreamRegistrationDisplayStatus | null | undefined;
  registration: WasteStreamRegistrationCellRegistration | null | undefined;
}

/**
 * The state-assigned registration id, split out of the Registration cell into
 * its own "State ID" column. Only In Progress / Registered streams have a
 * registration to carry one; everything else — and an as-yet-unassigned id —
 * shows an em dash. Purely presentational; the Registration column owns the
 * click-to-open-drawer affordance.
 */
export function WasteStreamStateIdCell({
  displayStatus,
  registration,
}: WasteStreamStateIdCellProps) {
  const hasRegistration =
    displayStatus === WasteStreamRegistrationDisplayStatus.InProgress ||
    displayStatus === WasteStreamRegistrationDisplayStatus.Registered;
  const stateId = registration?.stateRegisteredId?.trim();

  if (!hasRegistration || !stateId) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Typography
      variant="body2"
      title={stateId}
      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      {stateId}
    </Typography>
  );
}
